import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { fmt, roundRect } from '../chart-utils';

const COLOR_A = '#7c9cff';
const COLOR_B = '#ffb86b';
const COLOR_GRID = 'rgba(255,255,255,0.08)';
const COLOR_LABEL = 'rgba(255,255,255,0.68)';
const COLOR_AXIS_LABEL = 'rgba(255,255,255,0.82)';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarChart {
  readonly labels = input<string[]>([]);
  readonly seriesA = input<number[]>([]);
  readonly seriesB = input<number[]>([]);

  private readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly destroyRef = inject(DestroyRef);
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    effect(() => {
      const canvasEl = this.canvasRef();
      const labels = this.labels();
      const a = this.seriesA();
      const b = this.seriesB();
      if (!canvasEl || !labels.length) return;
      const canvas = canvasEl.nativeElement;
      if (canvas.getBoundingClientRect().width === 0) return;
      this.draw(canvas, labels, a, b);
    });

    afterNextRender(() => {
      const canvasEl = this.canvasRef();
      if (!canvasEl) return;
      const canvas = canvasEl.nativeElement;
      this.draw(canvas, this.labels(), this.seriesA(), this.seriesB());
      this.resizeObserver = new ResizeObserver(() =>
        this.draw(canvas, this.labels(), this.seriesA(), this.seriesB()),
      );
      this.resizeObserver.observe(canvas.parentElement ?? canvas);
    });

    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }

  private draw(
    canvas: HTMLCanvasElement,
    labels: string[],
    seriesA: number[],
    seriesB: number[],
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const pad = { top: 22, right: 20, bottom: 50, left: 66 };
    const chartW = w - pad.left - pad.right;
    const chartH = h - pad.top - pad.bottom;
    const maxValue = Math.max(...seriesA, ...seriesB) * 1.15 || 1;
    const groupWidth = chartW / labels.length;
    const barWidth = Math.min(48, groupWidth * 0.24);

    ctx.clearRect(0, 0, w, h);
    ctx.font = '12px Inter, sans-serif';

    for (let i = 0; i <= 5; i++) {
      const y = pad.top + (chartH / 5) * i;
      ctx.beginPath();
      ctx.strokeStyle = COLOR_GRID;
      ctx.moveTo(pad.left, y);
      ctx.lineTo(w - pad.right, y);
      ctx.stroke();
      ctx.fillStyle = COLOR_LABEL;
      ctx.fillText(fmt(Math.round(maxValue - (maxValue / 5) * i)), 4, y + 4);
    }

    labels.forEach((label, i) => {
      const centerX = pad.left + groupWidth * i + groupWidth / 2;
      const p = seriesA[i] ?? 0;
      const c = seriesB[i] ?? 0;
      const pHeight = (p / maxValue) * chartH;
      const cHeight = (c / maxValue) * chartH;

      ctx.fillStyle = COLOR_A;
      roundRect(ctx, centerX - barWidth - 6, pad.top + chartH - pHeight, barWidth, pHeight, 10);
      ctx.fill();

      ctx.fillStyle = COLOR_B;
      roundRect(ctx, centerX + 6, pad.top + chartH - cHeight, barWidth, cHeight, 10);
      ctx.fill();

      ctx.fillStyle = COLOR_AXIS_LABEL;
      ctx.textAlign = 'center';
      ctx.fillText(label, centerX, h - 18);
      ctx.textAlign = 'start';
    });
  }
}
